import React from 'react';
import type { PushCampaignStats } from '../../services/resortAdminApi';

interface CampaignProgressProps {
  stats: PushCampaignStats;
}

export const CampaignProgress: React.FC<CampaignProgressProps> = props => {
  const processed = props.stats.sent_count + props.stats.failed_count + props.stats.skipped_count;
  const total = props.stats.recipients_total;
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  const skipReasons: { label: string; value: number; }[] = [
    { label: 'No device', value: props.stats.skipped_no_device },
    { label: 'Cancelled', value: props.stats.skipped_cancelled },
    { label: 'Account deleted', value: props.stats.skipped_account_deleted },
    { label: 'Account not active', value: props.stats.skipped_not_active },
  ];

  const failReasons: { label: string; value: number; }[] = [
    { label: 'Unregistered token', value: props.stats.failed_unregistered_token },
    { label: 'FCM error', value: props.stats.failed_fcm_error },
    { label: 'Max attempts', value: props.stats.failed_max_attempts },
    { label: 'Lease thrash', value: props.stats.failed_lease_thrash },
  ];

  const excluded: { label: string; value: number; }[] = [
    { label: 'Not active', value: props.stats.excluded_not_active },
    { label: 'No email', value: props.stats.excluded_no_email },
    { label: 'No device', value: props.stats.excluded_no_device },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{processed} of {total} recipients processed</span>
          <span>{percent}%</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div className="h-2 rounded-full bg-alps-primary" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {renderCounterTile('Sent', props.stats.sent_count, 'Accepted by FCM, delivery is not guaranteed')}
        {renderCounterTile('Failed', props.stats.failed_count, null)}
        {renderCounterTile('Skipped', props.stats.skipped_count, null)}
        {renderCounterTile('Devices accepted', props.stats.devices_accepted_total, null)}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {renderBreakdown('Skip reasons', skipReasons)}
        {renderBreakdown('Failure reasons', failReasons)}
        {renderBreakdown('Excluded before launch', excluded)}
      </div>
    </div>
  );
};

function renderCounterTile(label: string, value: number, hint: string | null): React.ReactNode {
  return (
    <div key={label} className="rounded-lg border border-gray-200 p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {hint != null && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function renderBreakdown(title: string, rows: { label: string; value: number; }[]): React.ReactNode {
  return (
    <div key={title}>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <dl className="mt-2 space-y-1">
        {rows.map(x => (
          <div key={x.label} className="flex justify-between text-sm">
            <dt className="text-gray-600">{x.label}</dt>
            <dd className="font-medium text-gray-900">{x.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
